import React, { useContext, useEffect, useMemo, useState } from "react";
import { BrowserHistoryLeaveGuard } from "../navigation/HistoryLeaveGuard";
import { overrideNavigator } from "../navigation/overrideNavigator";
import {
    BrowserRouter,
    createPath,
    UNSAFE_NavigationContext as NavigationContext,
} from "react-router-dom";
import { tenantRoute, scopedBrowserPath } from "../utils/tenantRoute";
import { routeContract } from "../utils/routeContract";
import { samePagePath } from "../utils/authReturn";
import { requestLeave } from "../shell/leaveGuardStore";
import {
    closeTenantScopeForDocument,
    isTenantScopeValidated,
    leaveTenantScope,
} from "./tenantScopeHeaders";

/** Normalize where Router adds its basename, covering Link, Navigate and useNavigate. */
function ScopedNavigator({ children }: { children: React.ReactNode }) {
    const context = useContext(NavigationContext);
    const value = useMemo(() => {
        const normalize = (to: Parameters<typeof context.navigator.push>[0]) => {
            const path = typeof to === "string" ? to : createPath(to);
            const base = tenantRoute().basename;
            if (!base || !(path === base || path.startsWith(`${base}/`))) return path;
            const suffix = path.slice(base.length) || "/";
            // Explicit canonical destinations were prefixed by Router too.
            if (tenantRoute(suffix).basename) return suffix;
            if (routeContract(suffix)?.scope !== "project") return suffix;
            return path;
        };
        const move =
            (method: "push" | "replace") =>
            (...args: Parameters<typeof context.navigator.push>) => {
                const [to, state, options] = args;
                const path = normalize(to);
                // Only a path on this origin is followed. A destination such as `/\host`, which
                // a link built from server data can carry, would leave the app: the browser
                // reads it as `//host` once it is a full-document load below.
                if (!samePagePath(path)) {
                    console.warn("Navigation refused: not a path in this app", path);
                    return;
                }
                if (tenantRoute(path).basename !== tenantRoute().basename) {
                    requestLeave(() => {
                        // TenantScopeProvider closes on pagehide. Do not close
                        // before a native unload prompt can cancel the move.
                        if (method === "replace") window.location.replace(path);
                        else window.location.assign(path);
                    });
                    return;
                }
                context.navigator[method](path, state, options);
            };
        return {
            ...context,
            navigator: overrideNavigator(context.navigator, {
                createHref: (to: Parameters<typeof context.navigator.createHref>[0]) =>
                    context.navigator.createHref(normalize(to)),
                push: move("push"),
                replace: move("replace"),
            }),
        };
    }, [context]);
    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function TenantRouter({ children }: { children: React.ReactNode }) {
    const [basename, setBasename] = useState(() => tenantRoute().basename);
    useEffect(() => {
        const update = () => {
            const route = tenantRoute();
            const next = route.basename;
            if (next === basename) return;
            if (isTenantScopeValidated()) {
                closeTenantScopeForDocument();
                window.location.reload();
            } else {
                if (!route.companySlug) leaveTenantScope();
                setBasename(next);
            }
        };
        window.addEventListener("popstate", update);
        return () => window.removeEventListener("popstate", update);
    }, [basename]);
    useEffect(() => {
        // Rendered HTML links expose the correct destination even before interaction.
        const scopeAnchor = (anchor: HTMLAnchorElement) => {
            if (anchor.hasAttribute("download")) return;
            const raw = anchor.getAttribute("href");
            if (!raw) return;
            const scoped = scopedBrowserPath(raw);
            if (raw !== scoped) anchor.setAttribute("href", scoped);
        };
        const scopeTree = (node: Node) => {
            if (!(node instanceof Element)) return;
            if (node instanceof HTMLAnchorElement) scopeAnchor(node);
            node.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(scopeAnchor);
        };
        scopeTree(document.documentElement);
        const observer = new MutationObserver((records) => {
            for (const record of records) {
                if (record.type === "attributes") scopeTree(record.target);
                else record.addedNodes.forEach(scopeTree);
            }
        });
        observer.observe(document.documentElement, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ["href"],
        });
        const scopeLink = (event: Event) => {
            const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
            if (anchor instanceof HTMLAnchorElement) scopeAnchor(anchor);
        };
        for (const name of ["click", "auxclick", "contextmenu"])
            document.addEventListener(name, scopeLink, true);
        return () => {
            observer.disconnect();
            for (const name of ["click", "auxclick", "contextmenu"])
                document.removeEventListener(name, scopeLink, true);
        };
    }, [basename]);
    return (
        <BrowserRouter basename={basename || undefined} useTransitions={false}>
            <BrowserHistoryLeaveGuard />
            <ScopedNavigator>{children}</ScopedNavigator>
        </BrowserRouter>
    );
}
